import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search, AlertCircle, Printer, Download, ArrowRight, CheckCircle2, Star, Send } from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFAB from "@/components/shared/WhatsAppFAB";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, unwrap, API_BASE } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { openReceiptWindow } from "@/components/shared/BookingReceipt";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending_payment: "bg-amber-100 text-amber-800 border-amber-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  assigned: "bg-sky-100 text-sky-800 border-sky-300",
  in_progress: "bg-indigo-100 text-indigo-800 border-indigo-300",
  completed: "bg-teal-100 text-teal-800 border-teal-300",
  cancelled: "bg-stone-200 text-stone-700 border-stone-300",
  refunded: "bg-rose-100 text-rose-800 border-rose-300",
};

const STAGES = ["pending_payment", "paid", "assigned", "in_progress", "completed"];

// Only charged bookings have a receipt (mirrors RECEIPT_STATUSES on the backend).
const RECEIPT_STATUSES = ["paid", "assigned", "in_progress", "completed", "refunded"];

function StageProgress({ status }) {
  const idx = STAGES.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-4" data-testid="track-stage-progress">
      {STAGES.map((s, i) => {
        const reached = idx >= 0 && i <= idx;
        return (
          <React.Fragment key={s}>
            <div className={`w-3.5 h-3.5 rounded-full ${reached ? "bg-[#0EA5A4]" : "bg-[#E5E7EB]"} ${i === idx ? "ring-4 ring-[#0EA5A4]/20" : ""}`} />
            {i < STAGES.length - 1 && <div className={`flex-1 h-0.5 ${reached && idx > i ? "bg-[#0EA5A4]" : "bg-[#E5E7EB]"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function TrackingPage() {
  const { bookingNumber } = useParams();
  const { t, locale } = useI18n();
  const [input, setInput] = useState(bookingNumber || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchBooking = async (num) => {
    if (!num) return;
    setLoading(true); setNotFound(false);
    try {
      const r = await api.get(`/track/${num.trim().toUpperCase()}`);
      setData(unwrap(r));
    } catch {
      setData(null);
      setNotFound(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (bookingNumber) fetchBooking(bookingNumber); /* eslint-disable-next-line */ }, [bookingNumber]);

  const submit = (e) => { e.preventDefault(); fetchBooking(input); };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="tracking-page">
      <SEO
        title="Track your booking — Ever Clean Home"
        description="Live status for your Ever Clean Home cleaning. Enter your booking number to see scheduling, payment and service progress."
      />
      <Header />
      <WhatsAppFAB />

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{t("tracking.title")}</h1>
          <p className="mt-2 text-[#4B5563]">{t("tracking.subtitle")}</p>

          <form onSubmit={submit} className="mt-6 flex gap-2" data-testid="track-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 w-4 h-4 text-[#9CA3AF]" />
              <Input value={input} onChange={(e) => setInput(e.target.value)}
                placeholder={t("tracking.placeholder")} className="rounded-xl pl-9 h-11 font-mono uppercase tracking-wider"
                data-testid="track-input" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-xl h-11 bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="track-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("tracking.track_btn")}
            </Button>
          </form>
        </motion.div>

        {notFound && (
          <Card className="mt-8 rounded-2xl p-6 border border-[#E5E7EB] bg-amber-50/40" data-testid="track-not-found">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
              <div>
                <div className="font-semibold">{t("tracking.not_found_title")}</div>
                <p className="text-sm text-[#4B5563]">{t("tracking.not_found_body")}</p>
              </div>
            </div>
          </Card>
        )}

        {data && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="mt-8 rounded-2xl p-6 border border-[#E5E7EB]" data-testid="track-result-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">{t("tracking.booking_label")}</div>
                  <div className="font-mono font-bold text-2xl mt-1" data-testid="track-booking-number">{data.booking_number}</div>
                  <div className="text-sm text-[#4B5563] mt-1">{t("tracking.greeting", { name: data.customer_name })}</div>
                </div>
                <Badge className={`border font-medium capitalize ${STATUS_BADGE[data.status] || ""}`} data-testid="track-status-badge">
                  {data.status.replace("_", " ")}
                </Badge>
              </div>

              <StageProgress status={data.status} />

              <div className="mt-6 pt-6 border-t border-[#E5E7EB] grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-[#4B5563]">{t("tracking.zone")}</span><span className="font-semibold text-right">{data.zone_name}</span>
                <span className="text-[#4B5563]">{t("tracking.property")}</span><span className="font-semibold text-right capitalize">{data.property_type}</span>
                <span className="text-[#4B5563]">{t("tracking.size")}</span><span className="font-mono font-semibold text-right">{data.square_feet} sq ft</span>
                {data.property_type === "other" && data.space_description ? (
                  <><span className="text-[#4B5563]">{t("tracking.space_type")}</span><span className="font-semibold text-right">{data.space_description}</span></>
                ) : (
                  <><span className="text-[#4B5563]">{t("tracking.rooms")}</span><span className="font-mono font-semibold text-right">{data.bedrooms} / {data.bathrooms} (+{data.half_bathrooms} ½)</span></>
                )}
                {data.preferred_date && <><span className="text-[#4B5563]">{t("tracking.date")}</span><span className="font-semibold text-right">{data.preferred_date} · {data.preferred_time_slot}</span></>}
                <span className="text-[#4B5563]">{t("tracking.area")}</span><span className="font-semibold text-right">{data.city}, {data.state} {data.zip_code}</span>
              </div>

              <div className="mt-6 pt-6 border-t border-[#E5E7EB] flex items-center justify-between">
                <span className="text-sm text-[#4B5563]">{t("tracking.total")}</span>
                <span className="font-mono text-3xl font-bold text-[#0EA5A4]">${(data.total || 0).toFixed(2)}</span>
              </div>

              {data.status_history?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                  <div className="text-xs uppercase tracking-wider text-[#4B5563] font-bold mb-3">{t("tracking.activity")}</div>
                  <ol className="space-y-2">
                    {data.status_history.map((s, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#0EA5A4]" />
                        <span className="capitalize font-semibold">{s.status.replace("_", " ")}</span>
                        <span className="text-[#9CA3AF] text-xs ml-auto">{formatDateTime(s.at, locale)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="mt-7 flex flex-wrap gap-2">
                <Button onClick={() => openReceiptWindow({
                  booking_number: data.booking_number,
                  customer: {
                    full_name: data.customer_name, email: data.email, phone: data.phone,
                    address_line1: data.address_line1, address_line2: data.address_line2,
                    city: data.city, state: data.state, zip_code: data.zip_code,
                  },
                  service: {
                    zone_name: data.zone_name, property_type: data.property_type, square_feet: data.square_feet,
                    bedrooms: data.bedrooms, bathrooms: data.bathrooms, half_bathrooms: data.half_bathrooms,
                    tasks: data.tasks, additional_notes: data.additional_notes, space_description: data.space_description,
                    preferred_date: data.preferred_date, preferred_time_slot: data.preferred_time_slot,
                  },
                  pricing: data.pricing,
                  payment: { paid_at: data.paid_at },
                })} variant="outline" className="rounded-xl" data-testid="track-receipt-btn">
                  <Printer className="w-4 h-4 mr-2" /> {t("tracking.print_receipt")}
                </Button>
                {data.receipt_token && RECEIPT_STATUSES.includes(data.status) && (
                  <Button asChild variant="outline" className="rounded-xl" data-testid="track-download-pdf-btn">
                    <a href={`${API_BASE}/bookings/${data.booking_number}/receipt.pdf?token=${encodeURIComponent(data.receipt_token)}`}>
                      <Download className="w-4 h-4 mr-2" /> {t("tracking.download_pdf")}
                    </a>
                  </Button>
                )}
                <Link to="/book">
                  <Button className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="track-book-again">
                    {t("tracking.book_again")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </Card>

            {(data.status === "in_progress" || data.status === "completed") && (
              <ReviewSubmitter bookingNumber={data.booking_number} t={t} />
            )}

            <p className="text-center text-xs text-[#9CA3AF] mt-6">
              {t("tracking.help")}
            </p>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function ReviewSubmitter({ bookingNumber, t }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error(t("tracking.review_pick_rating")); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { booking_number: bookingNumber, rating, comment });
      setDone(true);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "already_reviewed") setAlready(true);
      else toast.error(err?.response?.data?.detail?.message || t("tracking.review_error"));
    } finally { setSubmitting(false); }
  };

  if (already) {
    return (
      <Card className="rounded-2xl p-5 mt-5 border border-[#E5E7EB] text-center text-sm text-[#4B5563]" data-testid="review-already">
        {t("tracking.review_already")}
      </Card>
    );
  }
  if (done) {
    return (
      <Card className="rounded-2xl p-5 mt-5 border border-emerald-200 bg-emerald-50/40 text-center" data-testid="review-thanks">
        <CheckCircle2 className="w-7 h-7 text-emerald-600 mx-auto mb-2" />
        <div className="font-display font-bold">{t("tracking.review_thanks")}</div>
        <div className="text-sm text-[#4B5563] mt-1">{t("tracking.review_thanks_sub")}</div>
      </Card>
    );
  }
  return (
    <Card className="rounded-2xl p-5 mt-5 border border-[#E5E7EB]" data-testid="review-form">
      <div className="text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">{t("tracking.review_title")}</div>
      <div className="font-display font-bold text-lg mt-1">{t("tracking.review_q")}</div>
      <div className="flex gap-1 mt-3" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button"
            onMouseEnter={() => setHover(n)}
            onClick={() => setRating(n)}
            className="p-1"
            aria-label={`Rate ${n} stars`}
            data-testid={`review-star-${n}`}>
            <Star className="w-7 h-7 text-[#FACC15] transition-colors"
              fill={(hover || rating) >= n ? "currentColor" : "none"} strokeWidth={1.5} />
          </button>
        ))}
      </div>
      <Textarea rows={3} placeholder={t("tracking.review_placeholder")}
        value={comment} onChange={(e) => setComment(e.target.value)}
        className="rounded-xl mt-3" data-testid="review-comment" />
      <Button onClick={submit} disabled={submitting || !rating}
        className="mt-3 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="review-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {t("tracking.review_submit")}</>}
      </Button>
    </Card>
  );
}
